import { create } from 'zustand';

export type UploadStatus = 'uploading' | 'success' | 'error';

export interface UploadItem {
    id: string;
    fileName: string;
    fileSize: number;
    progress: number;
    status: UploadStatus;
    url?: string;
    error?: string;
}

interface UploadStore {
    uploads: Record<string, UploadItem>;
    addUpload: (file: File) => string;
    updateProgress: (id: string, progress: number) => void;
    setSuccess: (id: string, url: string) => void;
    setError: (id: string, error: string) => void;
    removeUpload: (id: string) => void;
    clearCompleted: () => void;
}

export const useUploadStore = create<UploadStore>((set) => ({
    uploads: {},
    
    addUpload: (file: File) => {
        const id = crypto.randomUUID();
        set((state) => ({
            uploads: {
                ...state.uploads,
                [id]: {
                    id,
                    fileName: file.name,
                    fileSize: file.size,
                    progress: 0,
                    status: 'uploading',
                }
            }
        }));
        return id;
    },

    updateProgress: (id: string, progress: number) => {
        set((state) => {
            if (!state.uploads[id]) return state;
            return {
                uploads: {
                    ...state.uploads,
                    [id]: {
                        ...state.uploads[id],
                        progress: Math.min(progress, 100),
                    }
                }
            };
        });
    },

    setSuccess: (id: string, url: string) => {
        set((state) => {
            if (!state.uploads[id]) return state;
            return {
                uploads: {
                    ...state.uploads,
                    [id]: {
                        ...state.uploads[id],
                        status: 'success',
                        progress: 100,
                        url,
                    }
                }
            };
        });
    },

    setError: (id: string, error: string) => {
        set((state) => {
            if (!state.uploads[id]) return state;
            return {
                uploads: {
                    ...state.uploads,
                    [id]: {
                        ...state.uploads[id],
                        status: 'error',
                        error,
                    }
                }
            };
        });
    },

    removeUpload: (id: string) => {
        set((state) => {
            const newUploads = { ...state.uploads };
            delete newUploads[id];
            return { uploads: newUploads };
        });
    },

    clearCompleted: () => {
        set((state) => {
            const newUploads = { ...state.uploads };
            Object.keys(newUploads).forEach(id => {
                if (newUploads[id].status === 'success' || newUploads[id].status === 'error') {
                    delete newUploads[id];
                }
            });
            return { uploads: newUploads };
        });
    }
}));
